import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import {
  ClientService,
  ContractorService,
  DataEntityFactory,
  DataService,
  WorkflowService,
  WorkflowContextService,
  ValidationService
} from 'src/app/services';

import {
  Client,
  Contractor,
  ContractorAddress,
  ContractorEmailAddress,
  ContractorPhoneNumber,
  ContractorType,
  Document as WorkflowDocument
} from 'src/app/models';
import { ContractorDocument } from 'src/app/models/contractor-document';
import { ContractorDocumentCategory } from 'src/app/models/contractor-document-category';

import { ContractorCustomFieldListComponent } from 'src/app/components/contractors/contractor-custom-field-list/contractor-custom-field-list.component';
import { CommonTableComponent } from 'src/app/components/common/common-table/common-table.component';
import {
  CommonDropdownData,
  DropdownOption
} from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import { FilterBuilderParam } from 'src/app/models/filter-builder';
import { ColumnOption } from 'src/app/models/grid-settings';
import { ComponentIds } from 'src/app/models/component-ids';
import { RegistrationStatus } from 'src/app/models/registration';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractorsJanusActionComponent } from '../../contractors-janus-table/cells-janus/contractors-janus-action-cell/contractors-janus-action.component';
import { StaffContractorRegistrationDetailsComponent } from '../../contractor-registration-details/contractor-registration-details.component';
import { ContractorsJanusApplicationComponent } from '../../contractors-janus-table/contractors-janus-application/contractors-janus-application.component';
import { ContractorsJanusRenewComponent } from '../../contractors-janus-table/contractors-janus-renew/contractors-janus-renew.component';

type ContractorProfileTab =
  | 'permits'
  | 'profile'
  | 'registrations'
  | 'documents';

type InlineRegistrationView =
  | 'details'
  | 'application'
  | 'renew';

const REGISTRATION_STATUS_CONFIG = {
  disableAllowed: ['approved', 'denied', 'inactive'],
  enableAllowed: ['disabled'],
  deleteBlocked: ['deleted'],
  renewBlocked: ['pending', 'deleted']
};

@Component({
  selector: 'app-contractors-janus-profile-view',
  standalone: false,
  templateUrl:
    './contractors-janus-profile-view.component.html',
  styleUrls: [
    './contractors-janus-profile-view.component.scss'
  ]
})
export class ContractorsJanusProfileViewComponent
  implements OnInit, AfterViewInit
{
  @Input() row: any;

  @Input() registrationId!: string;

  @Input() contractorId!: string;

  @Input() closeExpansion!: () => void;

  @ViewChild('customFields')
  customFields?: ContractorCustomFieldListComponent;
  @ViewChild('registrationTable')
  registrationTable?: CommonTableComponent;

  contractor: Contractor | null = null;

  documentCategories: ContractorDocumentCategory[] = [];

  profileForm: UntypedFormGroup;

  activeTab: ContractorProfileTab = 'profile';

  isLoadingProfile = false;
  isLoadingRegistrations = false;

  isSavingProfile = false;

  isLoadingPermits = false;
  permitRows: any[] = [];
  permitPageNumber = 1;
  permitPageSize = 25;
  permitTotalCount = 0;
  permitSortField = 'submittedOn';
  permitSortDescending = true;
  private permitsLoaded = false;

  useExcelFormat = false;
  expandedComponent: any = null;
  expandedComponentInputs: any = null;
  registrationRowsData: any[] = [];
  attributeFilters: ItemSearchOptionField[] = [];
  registrationCurrentPage = 0;
  registrationPageSize = 10;
  registrationTotalPages = 0;
  registrationSortField = 'registeredOn';
  registrationSortDescending = true;
  selectedRegistrationRow: any = null;
  currentRegistrationAction:
    | 'disable'
    | 'enable'
    | 'delete'
    | null = null;
  isNewRegistrationOverlayOpen = false;
  isLoadingRegistrationClients = false;
  isLoadingRegistrationTypes = false;
  isStartingRegistration = false;
  newRegistrationClientId = '';
  newRegistrationTypeId = '';
  newRegistrationTypes: ContractorType[] = [];
  contractorClients: Client[] = [];
  contractorClientStates: string[] = [];

  readonly exportComponentId =
    ComponentIds.CONTRACTOR_PROFILE;
  readonly registrationColumnOptions: ColumnOption[] = [
    { name: 'registrationDate', label: 'Registration Date', checked: true, includeInExport: true },
    { name: 'effectiveDate', label: 'Effective Date', checked: true, includeInExport: true },
    { name: 'expirationDate', label: 'Expiration Date', checked: true, includeInExport: true },
    { name: 'approvedDate', label: 'Approved Date', checked: true, includeInExport: true },
    { name: 'contractorType', label: 'Contractor Type', checked: true, includeInExport: true },
    { name: 'paymentMethod', label: 'Payment Method', checked: true, includeInExport: true }
  ];
  readonly registrationFilterCriteria: FilterBuilderParam[] = [
  {
    id: 'registeredOn',
    name: 'Registration Date',
    inputType: 'date',
    types: ['is', 'range']
  },
  {
    id: 'effectiveOn',
    name: 'Effective Date',
    inputType: 'date',
    types: ['is', 'range']
  },
  {
    id: 'expiresOn',
    name: 'Expiration Date',
    inputType: 'date',
    types: ['is', 'range']
  },
  {
    id: 'approvedOn',
    name: 'Approved Date',
    inputType: 'date',
    types: ['is', 'range']
  },
  {
    id: 'type',
    name: 'Contractor Type'
  },
  {
    id: 'paymentMethod',
    name: 'Payment Method'
  }
];
  registrationFilters: ItemSearchOptionField[] = [];
  modalData: any = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: false,
    okBtn: false,
    closeBtn: false,
    inTime: 150,
    outTime: 150
  };

  readonly onSaveButtonClick = (): void => {
    this.saveProfile();
  };

  readonly tabs = [
    {
      id: 'profile',
      label: 'Profile'
    },
    {
      id: 'permits',
      label: 'Permits'
    },
    {
      id: 'registrations',
      label: 'Registrations'
    },
    {
      id: 'documents',
      label: 'Documents'
    }
  ] as const;

  readonly permitColumns = [
    {
      key: 'applicationNumber',
      label: 'App #',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'applicationType',
      label: 'App Type',
      width: '220px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'applicantName',
      label: 'Applicant Name',
      width: '170px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'siteAddress',
      label: 'Site Address',
      width: '220px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'fee',
      label: 'Fee Due',
      width: '110px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'departmentName',
      label: 'Department',
      width: '160px',
      headerType: 'text',
      cellType: 'text'
    }
  ] as any[];

  readonly registrationColumns = [
    {
      key: 'registrationDate',
      label: 'Registration Date',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'effectiveDate',
      label: 'Effective Date',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'expirationDate',
      label: 'Expiration Date',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'approvedDate',
      label: 'Approved Date',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'contractorType',
      label: 'Contractor Type',
      width: '190px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      width: '150px',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'action',
      label: 'Actions',
      width: '100px',
      headerType: 'text',
      cellType: 'component',
      component: ContractorsJanusActionComponent,
      componentInputs: (row: any) => ({
        row,
        viewDetails: (item: any) => this.openInline('details', item),
        viewApplication: (item: any) => this.openInline('application', item),
        renew: (item: any) => this.openInline('renew', item),
        delete: (item: any) => this.openRegistrationActionModal(item, 'delete'),
        disable: (item: any) => this.openRegistrationActionModal(item, 'disable'),
        enable: (item: any) => this.openRegistrationActionModal(item, 'enable'),
        canRenew: (item: any) => this.canRenew(item),
        canDelete: (item: any) => this.canDelete(item),
        canDisable: (item: any) => this.canDisable(item),
        canEnable: (item: any) => this.canEnable(item)
      })
    }
  ] as any[];


  // Added by Amisha on 2026-07-23.
  // Reason: keep the jurisdiction control interactive during preloading and
  // let its panel use content-driven height instead of a fixed empty area.
  get jurisdictionDropdownData(): CommonDropdownData {
    return {
      placeholder: 'Select Jurisdiction',
      selectedValue: this.newRegistrationClientId || null,
      disabled: this.isStartingRegistration,
      className: 'new-registration-common-dropdown',
      options: this.jurisdictionDropdownOptions,
      style: {
        width: '100%',
        height: '34px',
        border: '1px solid #cccccc',
        borderRadius: '3px',
        background: '#ffffff'
      }
    };
  }

  // Added by Amisha on 2026-07-23.
  // Reason: let the contractor-type panel shrink for short lists and rely on
  // the profile-scoped maximum height when more options require scrolling.
  get contractorTypeDropdownData(): CommonDropdownData {
    return {
      placeholder: 'Select Contractor Type',
      selectedValue: this.newRegistrationTypeId || null,
      disabled:
        !this.newRegistrationClientId ||
        this.isLoadingRegistrationTypes ||
        this.isStartingRegistration,
      className: 'new-registration-common-dropdown',
      options: this.newRegistrationTypes.map(type => ({
        label: type.name,
        value: type.id
      })),
      style: {
        width: '100%',
        height: '34px',
        border: '1px solid #cccccc',
        borderRadius: '3px',
        background: '#ffffff'
      }
    };
  }

  get jurisdictionDropdownOptions(): DropdownOption[] {
    const options: DropdownOption[] = [];

    this.contractorClientStates.forEach(state => {
      options.push({
        label: state,
        value: `state-${state}`,
        disabled: true,
        className: 'dropdown-state-header'
      });

      this.contractorClients
        .filter(client => client.state === state)
        .forEach(client => {
          options.push({
            label: client.name,
            value: client.id
          });
        });
    });

    this.contractorClients
      .filter(client => !client.state)
      .forEach(client => {
        options.push({
          label: client.name,
          value: client.id
        });
      });

    return options;
  }

  constructor(
    private contractorService: ContractorService,
    private dataService: DataService,
    private formBuilder: UntypedFormBuilder,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
    private context: WorkflowContextService,
    private clientService: ClientService,
    private workflowService: WorkflowService,
    private elementRef: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.profileForm = this.formBuilder.group({
      businessName: [
        '',
        Validators.required
      ],
      contactFirstName: [''],
      contactLastName: [''],
      emailAddress: [
        '',
        ValidationService.emailValidator
      ],
      phoneNumber: [
        '',
        ValidationService.phoneNumberValidator
      ],
      cellNumber: [
        '',
        ValidationService.phoneNumberValidator
      ],
      faxNumber: [
        '',
        ValidationService.phoneNumberValidator
      ]
    });
  }

  ngOnInit(): void {
    this.isLoadingProfile = true;
    this.loadContractorProfile();
  }

  ngAfterViewInit(): void {}

  setTab(tab: ContractorProfileTab): void {
    this.activeTab = tab;

    if (
      tab === 'permits' &&
      !this.permitsLoaded
    ) {
      this.loadPermits();
    }

    if (tab === 'registrations') {
      // Added by Amisha on 2026-07-23.
      // Reason: preload jurisdictions before New Registration opens so the
      // first dropdown interaction is not delayed by the client request.
      this.loadRegistrationClients();
      this.ensureRegistrationData();
    }
  }

  get isActiveTabLoading(): boolean {
    return (
      (this.activeTab === 'permits' &&
        this.isLoadingPermits) ||
      (this.activeTab === 'registrations' &&
        this.isLoadingRegistrations)
    );
  }

  getFieldControl(controlName: string): AbstractControl | null {
    return this.profileForm.get(controlName);
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.getFieldControl(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.touched || control.dirty)
    );
  }

  loadContractorProfile(): void {
    const resolvedContractorId =
      this.contractorId ||
      this.row?.contractorId ||
      this.row?.contractor?.id;

    if (!resolvedContractorId) {
      this.isLoadingProfile = false;

      this.toastrService.warning(
        'Contractor ID is not available.'
      );

      return;
    }

    this.contractorId = resolvedContractorId;
    this.isLoadingProfile = true;
    this.contractor = null;

    this.contractorService
      .getContractorProfile(this.contractorId)
      .subscribe({
        next: result => {
          this.contractor = result.contractor;
          this.documentCategories =
            result.documentCategories || [];

          if (!this.contractor) {
            this.isLoadingProfile = false;

            this.toastrService.warning(
              'Contractor profile was not found.'
            );

            return;
          }

          this.initializeContractorModels();
          this.initializeCustomFields();
          this.patchProfileForm();
          this.registrationFilters =
            this.buildRegistrationFilters();

          this.isLoadingProfile = false;
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          console.error(
            'Contractor profile API error:',
            error
          );

          this.isLoadingProfile = false;

          this.toastrService.error(
            'Unable to load contractor profile.'
          );
        }
      });
  }

  loadPermits(): void {
    if (
      !this.contractorId ||
      this.isLoadingPermits
    ) {
      return;
    }

    this.isLoadingPermits = true;

    const contractorFilter =
      new ItemSearchOptionField({
        title: 'contractorId',
        options: [
          new ItemSearchOptionFieldOption({
            title: '',
            selected: true,
            searchText:
              this.contractorId,
            filterText:
              this.contractorId,
            searchOptionMethod:
              'simpleSearch'
          })
        ]
      });

    const filterOptions:
      ItemSearchOptionField[] = [
        contractorFilter
      ];

    this.dataService
      .getAllWorkflowApplications(
        null,
        this.permitSortField,
        this.permitSortDescending,
        filterOptions,
        this.permitPageNumber,
        this.permitPageSize,
        null,
        true
      )
      .subscribe({
        next: response => {
          const applications =
            this.extractPermitApplications(
              response
            );

          this.permitRows =
            applications.map(application =>
              this.mapApplicationToPermitRow(
                application
              )
            );

          this.permitTotalCount =
            this.extractPermitTotalCount(
              response,
              applications.length
            );

          this.permitsLoaded = true;
          this.isLoadingPermits = false;

          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          console.error(
            'Unable to load contractor permits:',
            error
          );

          this.permitRows = [];
          this.permitTotalCount = 0;
          this.permitsLoaded = false;
          this.isLoadingPermits = false;

          this.toastrService.error(
            'Unable to load contractor permits.'
          );
        }
      });
  }

  private extractPermitApplications(
    response: any
  ): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    return (
      response.workflowApplications ||
      response.applications ||
      response.items ||
      response.results ||
      response.data ||
      []
    );
  }

  private extractPermitTotalCount(
    response: any,
    fallbackCount: number
  ): number {
    return (
      response?.totalCount ??
      response?.recordCount ??
      response?.totalRecords ??
      fallbackCount
    );
  }

  private mapApplicationToPermitRow(
    application: any
  ): any {
    return {
      id:
        application.appId ||
        application.id ||
        application.workflowApplicationId,

      applicationNumber:
        application.combinedApplicationNumber ||
        application.clientApplicationNumber ||
        application.applicationNumber ||
        '-',

      applicationType:
        application.permitType ||
        application.name ||
        '-',

      applicantName:
        application.applicantsName ||
        this.getContractorContactName(),

      siteAddress:
        this.getPermitAddress(
          application
        ),

      fee:
        this.formatPermitFee(
          application.feeDollarsDue
        ),

      currentActivityName:
        application.currentActivityName ||
        '-',

      departmentName:
      application.departmentName || '-',  

      originalApplication:
        application
    };
  }

  private getPermitAddress(
    application: any
  ): string {
    const addressParts = [
      application.parcelAddress,
      application.parcelCity
    ].filter(
      value =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
    );

    return addressParts.length
      ? addressParts.join(', ')
      : '-';
  }

  private getContractorContactName(): string {
    if (!this.contractor) {
      return '-';
    }

    return (
      [
        this.contractor.contactFirstName,
        this.contractor.contactLastName
      ]
        .filter(Boolean)
        .join(' ')
        .trim() || '-'
    );
  }

  private formatPermitFee(
    value:
      | string
      | number
      | null
      | undefined
  ): string {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '$0.00';
    }

    const numericValue =
      Number(value);

    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    return numericValue.toLocaleString(
      'en-US',
      {
        style: 'currency',
        currency: 'USD'
      }
    );
  }

  ensureRegistrationData(): void {
    if (
      this.activeTab === 'registrations' &&
      this.registrationRowsData.length === 0
    ) {
      this.fetchRegistrations();
    }
  }

  onRegistrationPageChange(page: number): void {
    this.registrationCurrentPage = page;
    this.fetchRegistrations();
  }

  onRegistrationRowsPerPageChange(size: number): void {
    this.registrationPageSize = size;
    this.registrationCurrentPage = 0;
    this.fetchRegistrations();
  }

  onRegistrationSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    this.registrationSortField =
      event.sortField || 'registeredOn';
    this.registrationSortDescending =
      event.sortDescending == null
        ? true
        : event.sortDescending;
    this.registrationCurrentPage = 0;
    this.fetchRegistrations();
  }

  onRegistrationAttribute(_value: string): void {}

  onRegistrationFilterChanged(
    value: ItemSearchOptionField[]
  ): void {
    this.attributeFilters =
      this.cloneFilters(value);
    this.registrationCurrentPage = 0;
    this.fetchRegistrations();
  }

  removeRegistrationFilterChip(
    chip: ItemSearchOptionField
  ): void {
    this.attributeFilters =
      this.attributeFilters.filter(filter => {
        const currentOption =
          filter?.options?.[0];
        const chipOption =
          chip?.options?.[0];

        return !(
          filter.title === chip.title &&
          currentOption?.filterText ===
            chipOption?.filterText &&
          currentOption?.searchText ===
            chipOption?.searchText &&
          currentOption?.strOperator ===
            chipOption?.strOperator
        );
      });

    this.registrationCurrentPage = 0;
    this.fetchRegistrations();
  }

  getRegistrationFilterChipLabel(
    chip: ItemSearchOptionField
  ): string {
    const match =
      this.registrationFilterCriteria.find(
        filter => filter.id === chip.title
      );

    return match?.name || chip.title || '';
  }

  openNewRegistrationOverlay(): void {
    if (!this.contractorId) {
      this.toastrService.warning(
        'Contractor ID is not available.'
      );
      return;
    }

    this.isNewRegistrationOverlayOpen = true;
    this.resetNewRegistrationSelection();
    this.loadRegistrationClients();
    requestAnimationFrame(() =>
      this.scrollNewRegistrationModalIntoView()
    );
  }

  closeNewRegistrationOverlay(): void {
    if (this.isStartingRegistration) {
      return;
    }

    this.isNewRegistrationOverlayOpen = false;
    this.resetNewRegistrationSelection();
  }

  onNewRegistrationOverlayWheel(event: WheelEvent): void {
    const target = event.target as HTMLElement | null;

    if (target?.closest('.dropdown-panel')) {
      return;
    }

    const scrollParent = this.getScrollableParent(
      this.elementRef.nativeElement
    );

    if (!scrollParent) {
      return;
    }

    const deltaMultiplier =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? scrollParent.clientHeight
          : 1;

    event.preventDefault();
    event.stopPropagation();
    scrollParent.scrollTop += event.deltaY * deltaMultiplier;
  }

  onNewRegistrationClientChange(
    clientId: string
  ): void {
    this.newRegistrationClientId = clientId;
    this.newRegistrationTypeId = '';
    this.newRegistrationTypes = [];

    if (!clientId || !this.contractorId) {
      return;
    }

    this.isLoadingRegistrationTypes = true;

    this.contractorService
      .getAvailableContractorTypes(
        clientId,
        this.contractorId
      )
      .subscribe({
        next: types => {
          this.newRegistrationTypes = types || [];
          this.isLoadingRegistrationTypes = false;
        },
        error: error => {
          console.error(error);
          this.toastrService.error(
            'Unable to load contractor types.'
          );
          this.newRegistrationTypes = [];
          this.isLoadingRegistrationTypes = false;
        }
      });
  }

  startNewRegistrationApplication(): void {
    const selectedType =
      this.newRegistrationTypes.find(
        type => type.id === this.newRegistrationTypeId
      );

    if (!selectedType?.registerWorkflowId) {
      this.toastrService.warning(
        'Please select a contractor type.'
      );
      return;
    }

    if (this.isStartingRegistration) {
      return;
    }

    this.isStartingRegistration = true;

    this.workflowService
      .startApplication(
        selectedType.registerWorkflowId,
        false
      )
      .subscribe({
        next: result => {
          this.isStartingRegistration = false;
          this.isNewRegistrationOverlayOpen = false;

          this.openStartedRegistrationInJanus(
            result?.applicationId
          );
        },
        error: error => {
          console.error(error);
          this.toastrService.error(
            'Unable to start contractor registration application.'
          );
          this.isStartingRegistration = false;
        }
      });
  }

  private openStartedRegistrationInJanus(
    applicationId: string
  ): void {
    if (!applicationId) {
      this.toastrService.error(
        'Unable to open the started registration application.'
      );
      return;
    }

    const allContractorsRoute =
      this.getAllContractorsRoute();

    if (!allContractorsRoute) {
      this.toastrService.warning(
        'Unable to open the application view in Contractors.'
      );
      return;
    }

    this.context.applicationViewType = 'overlay';
    this.context.applicationViewType$.emit('overlay');

    this.router.navigate(
      ['application', applicationId],
      {
        relativeTo: allContractorsRoute,
        queryParams: {
          p: 1
        },
        queryParamsHandling: 'replace',
        state: {
          startingActivityId: '1',
          useNewLayout: this.context.useNewLayout
        }
      }
    ).then(() => {
      this.context.applicationViewType = 'overlay';
      this.context.applicationViewType$.emit('overlay');
    });
  }

  private getAllContractorsRoute(): ActivatedRoute | null {
    let currentRoute: ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      if (
        currentRoute.snapshot.routeConfig?.path ===
        'all'
      ) {
        return currentRoute;
      }

      currentRoute = currentRoute.parent;
    }

    return null;
  }

  private fetchRegistrations(): void {
    if (!this.context.client?.id || !this.contractorId) {
      return;
    }

    this.isLoadingRegistrations = true;
    this.registrationFilters =
      this.buildRegistrationFilters();

    this.contractorService
      .searchContractorRegistrations(
        this.context.client.id,
        this.registrationSortField,
        this.registrationSortDescending,
        this.registrationFilters,
        this.registrationCurrentPage,
        this.registrationPageSize
      )
      .subscribe({
        next: result => {
          this.registrationRowsData = (
            result?.contractorRegistrations || []
          ).map(registration =>
            this.mapRegistrationRow(registration)
          );
          this.registrationTotalPages =
            result?.pageCount || 0;
          this.isLoadingRegistrations = false;
        },
        error: error => {
          console.error(error);
          this.toastrService.error(
            'Unable to load contractor registrations.'
          );
          this.registrationRowsData = [];
          this.registrationTotalPages = 0;
          this.isLoadingRegistrations = false;
        }
      });
  }

  private mapRegistrationRow(registration: any): any {
    return {
      id: registration?.id,
      createdApplicationId:
        registration?.registerApplicationId,
      contractorId:
        registration?.contractorID,
      contractorTypeId:
        registration?.contractorTypeID ||
        registration?.contractorTypeId ||
        registration?.type?.id,
      registrationDate:
        registration?.registeredOnString || 'N/A',
      effectiveDate:
        registration?.effectiveOnString || 'N/A',
      expirationDate:
        registration?.expiresOnString || 'N/A',
      approvedDate:
        registration?.approvedOnString || 'N/A',
      contractorType:
        registration?.type?.name ||
        registration?.contractorType?.name ||
        'N/A',
      paymentMethod:
        registration?.paymentRequest?.paymentMethod ||
        'N/A',
      status:
        registration?.status != null
          ? RegistrationStatus[registration.status]
          : ''
    };
  }

  private buildRegistrationFilters(): ItemSearchOptionField[] {
    if (!this.contractorId) {
      return [];
    }

    const staticFilters = [
      {
        title: 'contractorId',
        options: [
          {
            filterText: this.contractorId,
            searchText: '',
            strOperator: 'is',
            selected: true
          } as any
        ]
      }
    ];

    const mergedFilters = this.cloneFilters([
      ...staticFilters,
      ...this.attributeFilters
    ]);

      // Copy filterText value into searchText
  mergedFilters.forEach(filter => {
    filter?.options?.forEach((option: any) => {
      if (option?.filterText) {
        option.searchText = option.filterText;
      }
    });
  });
  
    return mergedFilters.filter(filter => {
      const option: any = filter?.options?.[0];

      if (!option) {
        return false;
      }

      return !!(
        option.searchText ||
        option.filterText ||
        option.customValue?.start ||
        option.customValue?.end
      );
    });
  }

  private loadRegistrationClients(): void {
    // Added by Amisha on 2026-07-23.
    // Reason: tab preloading and modal opening can occur close together, so
    // reuse the active request rather than issuing duplicate API calls.
    if (
      this.contractorClients.length > 0 ||
      this.isLoadingRegistrationClients
    ) {
      return;
    }

    this.isLoadingRegistrationClients = true;

    this.clientService
      .getClientsWithContractors()
      .subscribe({
        next: clients => {
          this.contractorClients = clients || [];
          this.contractorClientStates = Array.from(
            new Set(
              this.contractorClients
                .map(client => client.state || '')
                .filter(state => state !== '')
            )
          );
          this.isLoadingRegistrationClients = false;
        },
        error: error => {
          console.error(error);
          this.toastrService.error(
            'Unable to load jurisdictions.'
          );
          this.contractorClients = [];
          this.contractorClientStates = [];
          this.isLoadingRegistrationClients = false;
        }
      });
  }

  private resetNewRegistrationSelection(): void {
    this.newRegistrationClientId = '';
    this.newRegistrationTypeId = '';
    this.newRegistrationTypes = [];
    this.isLoadingRegistrationTypes = false;
  }

  private scrollNewRegistrationModalIntoView(): void {
    const modal = this.elementRef.nativeElement.querySelector(
      '.new-registration-modal'
    ) as HTMLElement | null;

    if (!modal) {
      return;
    }

    const scrollParent =
      this.getScrollableParent(modal);
    const modalRect = modal.getBoundingClientRect();
    const topPadding = 16;

    if (!scrollParent) {
      window.scrollTo({
        top:
          window.scrollY +
          modalRect.top -
          topPadding,
        behavior: 'auto'
      });
      return;
    }

    const parentRect =
      scrollParent.getBoundingClientRect();
    const targetTop =
      scrollParent.scrollTop +
      modalRect.top -
      parentRect.top -
      topPadding;

    scrollParent.scrollTo({
      top: targetTop,
      behavior: 'auto'
    });
  }

  private getScrollableParent(
    element: HTMLElement
  ): HTMLElement | null {
    let parent = element.parentElement;

    while (parent) {
      const styles = window.getComputedStyle(parent);
      const overflowY = styles.overflowY;
      const canScroll =
        /(auto|scroll|overlay)/.test(overflowY) &&
        parent.scrollHeight > parent.clientHeight;

      if (canScroll) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  openInline(
    type: InlineRegistrationView,
    row: any
  ): void {
    if (!this.registrationTable || !row) {
      return;
    }

    this.closeInline();

    setTimeout(() => {
      switch (type) {
        case 'details':
          this.expandedComponent =
            StaffContractorRegistrationDetailsComponent;
          break;
        case 'application':
          if (!row.createdApplicationId) {
            this.showWarning(
              'Application is not available for this registration.'
            );
            return;
          }
          this.expandedComponent =
            ContractorsJanusApplicationComponent;
          break;
        case 'renew':
          this.expandedComponent =
            ContractorsJanusRenewComponent;
          break;
        default:
          return;
      }

      this.expandedComponentInputs = (
        tableRow: any
      ) => {
        const commonInputs = {
          row: tableRow,
          registrationId: tableRow.id,
          closeExpansion: () =>
            this.closeInline()
        };

        if (type === 'application') {
          return {
            ...commonInputs,
            applicationId:
              tableRow.createdApplicationId
          };
        }

        if (type === 'renew') {
          return {
            ...commonInputs,
            contractorId: tableRow.contractorId,
            contractorTypeId:
              tableRow.contractorTypeId
          };
        }

        return commonInputs;
      };

      this.registrationTable.expandRow(row);
    }, 0);
  }

  closeInline(): void {
    this.expandedComponent = null;
    this.expandedComponentInputs = null;

    if (
      this.registrationTable &&
      typeof this.registrationTable.closeExpansion ===
        'function'
    ) {
      this.registrationTable.closeExpansion();
      return;
    }

    if (this.registrationTable) {
      this.registrationTable.expandedRow = null;
    }
  }

  openRegistrationActionModal(
    row: any,
    action: 'disable' | 'enable' | 'delete'
  ): void {
    this.selectedRegistrationRow = row;
    this.currentRegistrationAction = action;

    if (action === 'disable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Disable Registration',
        modalSubDescp:
          'Are you sure you want to disable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'enable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Enable Registration',
        modalSubDescp:
          'This registration will be restored to a status determined by its expiration date and/or requirements.\n\nAre you sure you want to enable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'delete') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Delete Registration',
        modalSubDescp:
          'Are you sure you want to delete this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }
  }

  confirmRegistrationAction(): void {
    if (
      !this.selectedRegistrationRow?.id ||
      !this.currentRegistrationAction
    ) {
      return;
    }

    const row = this.selectedRegistrationRow;
    const action = this.currentRegistrationAction;

    this.modalData = {
      ...this.modalData,
      open: false
    };

    if (action === 'enable') {
      this.contractorService
        .reEnableContractorRegistration(row.id)
        .subscribe({
          next: (result: boolean) => {
            if (result) {
              this.fetchRegistrations();
              this.openSuccessModal(action);
            }
          },
          error: err => console.error(err)
        });
    }

    if (action === 'disable') {
      this.contractorService
        .disableContractorRegistration(row.id)
        .subscribe({
          next: (result: boolean) => {
            if (result) {
              this.fetchRegistrations();
              this.openSuccessModal(action);
            }
          },
          error: err => console.error(err)
        });
    }

    if (action === 'delete') {
      this.contractorService
        .deleteContractorRegistration(row.id)
        .subscribe({
          next: () => {
            this.fetchRegistrations();
            this.openSuccessModal(action);
          },
          error: err => console.error(err)
        });
    }

    this.currentRegistrationAction = null;
  }

  cancelRegistrationAction(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.currentRegistrationAction = null;
  }

  closeRegistrationModal(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.currentRegistrationAction = null;
    this.selectedRegistrationRow = null;
    this.registrationRowsData = [
      ...this.registrationRowsData
    ];
  }

  openSuccessModal(action: string): void {
    this.modalData = {
      ...this.modalData,
      open: true,
      modalDescp: 'Contractors',
      modalSubDescp:
        this.getSuccessMessage(action),
      yesNoBtn: false,
      okBtn: true,
      closeBtn: false
    };
  }

  getSuccessMessage(action: string): string {
    switch (action) {
      case 'disable':
        return 'Your registration has been disabled successfully.';
      case 'enable':
        return 'Your registration has been enabled successfully.';
      case 'delete':
        return 'Your registration has been deleted successfully.';
      default:
        return 'Action completed successfully.';
    }
  }

  private canRenew(item: any): boolean {
    return !REGISTRATION_STATUS_CONFIG.renewBlocked.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canDelete(item: any): boolean {
    return !REGISTRATION_STATUS_CONFIG.deleteBlocked.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canDisable(item: any): boolean {
    return REGISTRATION_STATUS_CONFIG.disableAllowed.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canEnable(item: any): boolean {
    return REGISTRATION_STATUS_CONFIG.enableAllowed.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  showWarning(message: string): void {
    this.toastrService.warning(message);
  }

  private cloneFilters(
    filters: ItemSearchOptionField[] = []
  ): ItemSearchOptionField[] {
    return JSON.parse(
      JSON.stringify(filters || [])
    );
  }

  private initializeContractorModels(): void {
    if (!this.contractor) {
      return;
    }

    if (!this.contractor.contactEmail) {
      this.contractor.contactEmail =
        new ContractorEmailAddress({
          parent: this.contractorId
        });
    }

    if (!this.contractor.businessPhone) {
      this.contractor.businessPhone =
        new ContractorPhoneNumber({
          parentID: this.contractorId
        });
    }

    if (!this.contractor.businessCell) {
      this.contractor.businessCell =
        new ContractorPhoneNumber({
          parentID: this.contractorId
        });
    }

    if (!this.contractor.contactFaxNumber) {
      this.contractor.contactFaxNumber =
        new ContractorPhoneNumber({
          parentID: this.contractorId
        });
    }

    if (!this.contractor.address) {
      this.contractor.address =
        new ContractorAddress({
          parent: this.contractorId
        });
    }

    this.contractor.registrations ??= [];
    this.contractor.documents ??= [];
    this.contractor.customFields ??= [];
  }

  private initializeCustomFields(): void {
    if (!this.contractor) {
      return;
    }

    this.contractor.customFields = (
      this.contractor.customFields || []
    ).map(customField => {
      const dataEntityTypeCode =
        customField
          .dataEntityDefinition
          ?.dataEntityTypeCode ||
        customField.dataEntityType ||
        'free-form-text';

      return {
        ...customField,

        dataEntityDef:
          customField.dataEntityDefinition ||
          DataEntityFactory.createDataEntity(
            dataEntityTypeCode,
            {
              templateCode:
                customField.templateCode,
              label:
                customField.label
            }
          ),

        dataEntityTypeCode,

        dataEntityType:
          dataEntityTypeCode
      };
    });
  }

  private patchProfileForm(): void {
    if (!this.contractor) {
      return;
    }

    this.profileForm.patchValue(
      {
        businessName:
          this.contractor.businessName || '',

        contactFirstName:
          this.contractor.contactFirstName || '',

        contactLastName:
          this.contractor.contactLastName || '',

        emailAddress:
          this.contractor.contactEmail?.email || '',

        phoneNumber:
          this.contractor.businessPhone?.number || '',

        cellNumber:
          this.contractor.businessCell?.number || '',

        faxNumber:
          this.contractor
            .contactFaxNumber
            ?.number || ''
      },
      {
        emitEvent: false
      }
    );

    this.profileForm.markAsPristine();
    this.profileForm.markAsUntouched();
  }

  saveProfile(): void {
    if (!this.contractor || this.isSavingProfile) {
      return;
    }

    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.profileForm.updateValueAndValidity();

      this.toastrService.warning(
        'Please correct the profile validation errors.'
      );

      return;
    }

    const formValue =
      this.profileForm.getRawValue();

    this.contractor.businessName =
      formValue.businessName;

    this.contractor.contactFirstName =
      formValue.contactFirstName;

    this.contractor.contactLastName =
      formValue.contactLastName;

    this.contractor.contactEmail.email =
      formValue.emailAddress;

    this.contractor.businessPhone.number =
      formValue.phoneNumber;

    this.contractor.businessCell.number =
      formValue.cellNumber;

    this.contractor.contactFaxNumber.number =
      formValue.faxNumber;

    if (this.customFields) {
      this.contractor.customFields =
        this.customFields.persistValues();
    }

    const registrations =
      this.contractor.registrations;

    const documents =
      this.contractor.documents;

    /*
     * Added by Amisha on 2026-07-17.
     * Reason: registration and document collections are not required
     * when saving contractor profile details.
     */
    this.contractor.registrations = [];
    this.contractor.documents = [];

    this.isSavingProfile = true;

    this.contractorService
      .saveContractorDetails(this.contractor)
      .subscribe({
        next: () => {
          if (!this.contractor) {
            this.isSavingProfile = false;
            return;
          }

          this.contractor.registrations =
            registrations;

          this.contractor.documents =
            documents;

          this.isSavingProfile = false;

          this.profileForm.markAsPristine();
          this.profileForm.markAsUntouched();

          this.toastrService.success(
            'Contractor profile saved successfully.'
          );
        },
        error: error => {
          console.error(
            'Unable to save contractor profile:',
            error
          );

          if (this.contractor) {
            this.contractor.registrations =
              registrations;

            this.contractor.documents =
              documents;
          }

          this.isSavingProfile = false;

          this.toastrService.error(
            'Unable to save contractor profile.'
          );
        }
      });
  }

  addNewDocuments(
    documents: WorkflowDocument[]
  ): void {
    if (
      !this.contractorId ||
      !documents?.length
    ) {
      return;
    }

    const contractorDocuments =
      documents.map(document => {
        const contractorDocument =
          new ContractorDocument();

        contractorDocument.contractorId =
          this.contractorId;
        contractorDocument.documentName =
          document.name;
        contractorDocument.documentPath =
          document.path;
        contractorDocument.createdOn =
          new Date().toUTCString();
        contractorDocument.modifiedOn =
          new Date().toUTCString();

        return contractorDocument;
      });

    this.contractorService
      .saveContractorDocuments(
        this.contractorId,
        contractorDocuments
      )
      .subscribe({
        next: documentsResult => {
          if (this.contractor) {
            this.contractor.documents = [
              ...(documentsResult || [])
            ];
          }

          this.toastrService.success(
            'Documents Added'
          );

          this.loadDocuments();
        },
        error: error => {
          console.error(
            'Unable to add contractor documents:',
            error
          );

          this.toastrService.error(
            'Unable to add documents.'
          );
        }
      });
  }

  deleteDocument(documentId: string): void {
    if (!documentId) {
      return;
    }

    this.contractorService
      .deleteContractorDocument(documentId)
      .subscribe({
        next: () => {
          this.toastrService.success(
            'Document Deleted'
          );

          this.loadDocuments();
        },
        error: error => {
          console.error(
            'Unable to delete contractor document:',
            error
          );

          this.toastrService.error(
            'Unable to delete document.'
          );
        }
      });
  }

  saveDocument(
    document: ContractorDocument
  ): void {
    if (!document) {
      return;
    }

    this.contractorService
      .saveContractorDocument(document)
      .subscribe({
        next: savedDocument => {
          this.toastrService.success(
            'Document Saved'
          );

          if (
            savedDocument &&
            this.contractor
          ) {
            this.contractor.documents =
              (this.contractor.documents || []).map(
                currentDocument =>
                  currentDocument.id ===
                  savedDocument.id
                    ? savedDocument
                    : currentDocument
              );
          }
        },
        error: error => {
          console.error(
            'Unable to save contractor document:',
            error
          );

          this.toastrService.error(
            'Unable to save document.'
          );
        }
      });
  }

  private loadDocuments(): void {
    if (
      !this.contractorId ||
      !this.contractor
    ) {
      return;
    }

    this.contractorService
      .getContractorDocuments(
        this.contractorId
      )
      .subscribe({
        next: documents => {
          if (!this.contractor) {
            return;
          }

          this.contractor.documents = [
            ...(documents || [])
          ];

          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          console.error(
            'Unable to load contractor documents:',
            error
          );

          this.toastrService.error(
            'Unable to refresh documents.'
          );
        }
      });
  }

  closeProfile(): void {
    if (
      typeof this.closeExpansion ===
      'function'
    ) {
      this.closeExpansion();
    }
  }

  get registrationRows(): any[] {
    return this.registrationRowsData;
  }

  get documentRows(): any[] {
    return (
      this.contractor?.documents || []
    );
  }

  get profileSummary(): {
    label: string;
    value: string;
  }[] {
    if (!this.contractor) {
      return [];
    }

    const contactName = [
      this.contractor.contactFirstName,
      this.contractor.contactLastName
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    const address =
      this.contractor.address as any;

    return [
      {
        label: 'Business Name',
        value:
          this.contractor.businessName || '-'
      },
      {
        label: 'Name',
        value:
          contactName || '-'
      },
      {
        label: 'Email',
        value:
          this.contractor
            .contactEmail?.email || '-'
      },
      {
        label: 'Phone',
        value:
          this.contractor
            .businessPhone?.number || '-'
      },
      {
        label: 'Cell Phone',
        value:
          this.contractor
            .businessCell?.number || '-'
      },
      {
        label: 'Fax',
        value:
          this.contractor
            .contactFaxNumber
            ?.number || '-'
      },
      {
        label: 'Street Number',
        value:
          address?.streetNumber ||
          address?.number ||
          '-'
      },
      {
        label: 'Street Name',
        value:
          address?.streetName ||
          address?.street ||
          '-'
      },
      {
        label: 'City',
        value:
          address?.city || '-'
      },
      {
        label: 'State',
        value:
          address?.state || '-'
      },
      {
        label: 'Zip',
        value:
          address?.zip ||
          address?.postalCode ||
          '-'
      }
    ];
  }
}
